import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  sitePhotosApi, projectsApi, usersApi,
  type SitePhotoPost, type SitePhoto, type SitePhotoPage,
} from '@/lib/apiClient';
import { useGraphToken, useThumbnailCache } from '@/lib/graphHooks';
import { fmtDate } from '@/lib/dates';
import { describeDistance, distanceMeters } from '@/lib/photoMeta';
import { viewerSequence } from '@/lib/sitePhotoOrder';
import { monthLabel, monthOf, monthOfRange, monthRange } from '@/lib/photoMonths';
import { Badge, Button } from '@/components';
import { useDebounced } from './BidLink';
import SitePhotoComposer from './SitePhotoComposer';
import SitePhotoViewer, { type ViewerItem } from './SitePhotoViewer';

/**
 * The site photo thread — every photo posted on a job, newest site day first,
 * whoever took it (#519). Each post shows who posted it and when, and each
 * photo when it was taken and where, when the phone recorded it (#328 asks for
 * capture-time order, which is how days and posts are sorted).
 *
 * Uses:
 *  - the job's Photos tab, in the Documents group (#665), and the "Photos"
 *    view of the Daily Logs tab — the SAME whole thread either way, with
 *    search, a month picker and filters by person and date. One feed with two
 *    ways in, not two galleries: both read GET /site-photos;
 *  - inside one daily log (`fixedDate`) — just that day, posting to that day.
 *
 * Files go to the Job Folder (▸ Daily Log Photos) with the signed-in user's own
 * Graph token, exactly like the old per-log Photos card; the tracker stores the
 * links and metadata.
 */

const PAGE_SIZE = 20;

const css = `
  .spt { display: grid; gap: var(--ew-s-4); }
  .spt-bar {
    display: flex; flex-wrap: wrap; align-items: flex-end; gap: var(--ew-s-3);
    padding: var(--ew-s-4);
  }
  .spt-bar-title { margin: 0; font: var(--ew-fw-semibold) var(--ew-fs-h4)/1.2 var(--ew-font); color: var(--ew-dark); }
  .spt-bar-sub { margin: 4px 0 0; font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font); color: var(--ew-n-500); }
  .spt-add { margin-left: auto; }
  .spt-filters {
    display: flex; flex-wrap: wrap; gap: var(--ew-s-2) var(--ew-s-3); align-items: flex-end;
    padding: 0 var(--ew-s-4) var(--ew-s-4);
  }
  .spt-filter { display: grid; gap: 4px; min-width: 140px; flex: 1 1 140px; max-width: 240px; }
  .spt-filter.spt-search { flex: 2 1 260px; max-width: 420px; }
  /* Its own row under the filters: inside the search cell it pushed that
     input above the others, since the row aligns cells by their bottoms. */
  .spt-hint { flex: 1 0 100%; margin: 0; font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-500); }
  .spt-month {
    margin: var(--ew-s-2) 0 0; padding-bottom: 6px; border-bottom: 1px solid var(--ew-n-200);
    font: var(--ew-fw-semibold) var(--ew-fs-h4)/1.2 var(--ew-font); color: var(--ew-dark);
  }
  .spt-label { font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); }
  .spt-input {
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 7px 10px; width: 100%;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-dark); background: #fff;
    box-sizing: border-box; min-height: 36px;
  }
  .spt-link {
    background: transparent; border: 0; padding: 6px 4px; cursor: pointer; color: var(--ew-green-700);
    font: var(--ew-fw-medium) var(--ew-fs-body-sm) var(--ew-font);
  }
  .spt-link:hover { text-decoration: underline; }
  .spt-muted { color: var(--ew-n-500); font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font); }
  .spt-error {
    padding: 8px 12px; background: #FBE4DF; color: #8F2414; border-radius: var(--ew-r-md);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
  }

  /* Timeline */
  .spt-day { display: grid; gap: var(--ew-s-3); }
  .spt-day-hd {
    display: flex; align-items: center; gap: var(--ew-s-2); flex-wrap: wrap;
    position: sticky; top: 0; z-index: 2; padding: 6px 0; background: var(--ew-n-50);
  }
  .spt-day-date { font: var(--ew-fw-semibold) var(--ew-fs-body-ui)/1.2 var(--ew-font); color: var(--ew-dark); }
  .spt-day-count { font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-n-500); }
  .spt-log-chip {
    margin-left: auto; display: inline-flex; align-items: center; gap: 6px;
    background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-pill);
    padding: 4px 10px; cursor: pointer; font: var(--ew-fw-medium) 12px/1 var(--ew-font); color: var(--ew-n-700);
  }
  .spt-log-chip:hover { border-color: var(--ew-green); color: var(--ew-green-700); }
  .spt-posts { display: grid; gap: var(--ew-s-3); border-left: 2px solid var(--ew-n-200); padding-left: var(--ew-s-3); margin-left: 6px; }

  /* Post */
  .spt-post { background: #fff; border: 1px solid var(--ew-n-100); border-radius: var(--ew-r-lg); box-shadow: var(--ew-shadow-1); overflow: hidden; }
  .spt-post-hd { display: flex; align-items: center; gap: var(--ew-s-2); padding: var(--ew-s-3) var(--ew-s-4) 0; }
  .spt-avatar {
    width: 32px; height: 32px; border-radius: 50%; flex: none; display: flex; align-items: center; justify-content: center;
    background: var(--ew-green-50); color: var(--ew-green-700); font: var(--ew-fw-semibold) 12px/1 var(--ew-font);
  }
  .spt-who { display: grid; gap: 2px; min-width: 0; }
  .spt-name { font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1.2 var(--ew-font); color: var(--ew-dark); }
  .spt-when { font: var(--ew-fw-regular) 12px/1.2 var(--ew-font); color: var(--ew-n-500); }
  .spt-actions { margin-left: auto; display: flex; gap: 2px; }
  .spt-caption { padding: var(--ew-s-2) var(--ew-s-4) 0; font: var(--ew-fw-regular) var(--ew-fs-body-ui)/1.45 var(--ew-font); color: var(--ew-dark); white-space: pre-wrap; margin: 0; }
  /* Tiles stay thumbnail-sized on a wide screen (more columns) and go three
     across on a phone. One or two photos show bigger, but not wall-sized. */
  .spt-grid { display: grid; gap: 3px; padding: var(--ew-s-3) var(--ew-s-4) 0; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); }
  .spt-grid.spt-one { grid-template-columns: minmax(0, 560px); }
  .spt-grid.spt-two { grid-template-columns: repeat(2, minmax(0, 280px)); }
  .spt-tile {
    position: relative; aspect-ratio: 1 / 1; border: 0; padding: 0; cursor: zoom-in; overflow: hidden;
    background: var(--ew-n-100); border-radius: var(--ew-r-md); display: block; width: 100%;
  }
  .spt-one .spt-tile { aspect-ratio: 4 / 3; }
  .spt-tile img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .spt-tile-ph {
    width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; padding: 6px;
    box-sizing: border-box; font: var(--ew-fw-regular) 11px/1.3 var(--ew-font); color: var(--ew-n-500); word-break: break-all;
  }
  .spt-tile-tag {
    position: absolute; left: 4px; bottom: 4px; padding: 2px 6px; border-radius: var(--ew-r-pill);
    background: rgba(0, 0, 0, 0.6); color: #fff; font: var(--ew-fw-medium) 11px/1.4 var(--ew-font);
  }
  .spt-tile-x {
    position: absolute; top: 4px; right: 4px; width: 26px; height: 26px; border-radius: 50%; border: 0;
    background: rgba(0, 0, 0, 0.55); color: #fff; cursor: pointer; font-size: 15px; line-height: 1;
  }
  .spt-tile-x:hover { background: var(--ew-danger); }
  .spt-meta { display: flex; flex-wrap: wrap; gap: 4px 12px; padding: var(--ew-s-2) var(--ew-s-4) 0; font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-500); }
  .spt-edit { padding: var(--ew-s-2) var(--ew-s-4) 0; display: grid; gap: var(--ew-s-2); }
  .spt-edit-row { display: flex; gap: var(--ew-s-2); justify-content: flex-end; }

  /* Comments */
  .spt-comments { margin-top: var(--ew-s-3); border-top: 1px solid var(--ew-n-100); padding: var(--ew-s-2) var(--ew-s-4) var(--ew-s-3); display: grid; gap: var(--ew-s-2); }
  .spt-comment { display: flex; gap: var(--ew-s-2); align-items: flex-start; }
  .spt-comment .spt-avatar { width: 24px; height: 24px; font-size: 10px; }
  .spt-comment-body { font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font); color: var(--ew-dark); white-space: pre-wrap; min-width: 0; flex: 1; }
  .spt-comment-body b { font-weight: var(--ew-fw-semibold); }
  .spt-comment-when { color: var(--ew-n-400); font-size: 11px; margin-left: 6px; }
  .spt-comment-x { background: transparent; border: 0; cursor: pointer; color: var(--ew-n-400); font-size: 15px; padding: 0 4px; }
  .spt-comment-x:hover { color: var(--ew-danger); }
  .spt-reply { display: flex; gap: var(--ew-s-2); }
  .spt-reply .spt-input { flex: 1; }
  .spt-icon {
    background: transparent; border: 0; cursor: pointer; color: var(--ew-n-500); border-radius: var(--ew-r-md);
    padding: 6px 8px; font: var(--ew-fw-medium) 12px/1 var(--ew-font);
  }
  .spt-icon:hover { background: var(--ew-n-50); color: var(--ew-dark); }
  .spt-icon.spt-danger:hover { color: var(--ew-danger); }
  .spt-more { display: flex; justify-content: center; }
  .spt-empty { padding: var(--ew-s-5) var(--ew-s-4); text-align: center; }
  .spt-unlinked {
    margin: 0 var(--ew-s-4) var(--ew-s-4); border: 1px dashed var(--ew-n-300); border-radius: var(--ew-r-md);
    padding: var(--ew-s-3); background: var(--ew-n-50);
  }
  @media (max-width: 600px) {
    .spt-bar { padding: var(--ew-s-3); }
    .spt-add { margin-left: 0; width: 100%; }
    .spt-add button { width: 100%; }
    .spt-filter { max-width: none; }
    .spt-posts { padding-left: var(--ew-s-2); margin-left: 2px; }
    .spt-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .spt-grid.spt-one { grid-template-columns: 1fr; }
    .spt-grid.spt-two { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .spt-grid, .spt-post-hd, .spt-caption, .spt-meta, .spt-edit, .spt-comments { padding-left: var(--ew-s-3); padding-right: var(--ew-s-3); }
  }
`;

function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function fmtClock(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function fmtPosted(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function dayLabel(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  if (!m) return fmtDate(ymd);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

/** "Taken 7:02–9:15 AM" across a post's photos, or null when none carry a time. */
function takenRange(photos: SitePhoto[]): string | null {
  const ts = photos.map((p) => p.takenAt).filter((t): t is string => !!t).map((t) => new Date(t).getTime()).sort((a, b) => a - b);
  if (ts.length === 0) return null;
  const a = fmtClock(new Date(ts[0]).toISOString());
  const b = fmtClock(new Date(ts[ts.length - 1]).toISOString());
  return a === b ? `Taken ${a}` : `Taken ${a} – ${b}`;
}

type Site = { latitude: number; longitude: number } | null;

/** Where a post's photos were, in one phrase: "On site", "2.3 mi from site", "Location recorded". */
function placeSummary(photos: SitePhoto[], site: Site): string | null {
  const located = photos.filter((p) => p.latitude != null && p.longitude != null);
  if (located.length === 0) return null;
  const fromPhone = located.every((p) => p.locationSource === 'device');
  if (!site) return fromPhone ? 'Location from phone' : 'Location recorded';
  const far = Math.max(...located.map((p) => distanceMeters(site, { latitude: p.latitude!, longitude: p.longitude! })));
  return `${describeDistance(far)}${fromPhone ? ' (phone)' : ''}`;
}


export default function SitePhotoThread({
  projectId,
  canPost,
  locked = false,
  fixedDate,
  onOpenLog,
}: {
  projectId: number;
  /** Mirrors the API's post rule (projects.edit OR daily_logs.edit). */
  canPost: boolean;
  /** Activity-locked job (SYSTAC / ClosedWon): the API refuses new posts. */
  locked?: boolean;
  /** Show and post to one site day only (used inside a daily log). */
  fixedDate?: string;
  onOpenLog?: (logId: number) => void;
}) {
  const qc = useQueryClient();
  const graph = useGraphToken();
  const getToken = graph.interactive;
  const fileRef = useRef<HTMLInputElement>(null);
  const [composerFiles, setComposerFiles] = useState<File[] | null>(null);
  const [viewer, setViewer] = useState<{ items: ViewerItem[]; index: number } | null>(null);
  const [authorId, setAuthorId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim(), 300);

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  const isBroadEditor = (me?.roles.includes('Admin') ?? false) || (me?.permissions.includes('projects.edit') ?? false);

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId) as Promise<{
      jobNumber: string; jobFolderUrl: string | null; jobLatitude?: number | null; jobLongitude?: number | null;
    }>,
  });
  const site: Site = project?.jobLatitude != null && project?.jobLongitude != null
    ? { latitude: Number(project.jobLatitude), longitude: Number(project.jobLongitude) }
    : null;

  const filters = fixedDate
    ? { from: fixedDate, to: fixedDate }
    : { authorId: authorId || undefined, from: from || undefined, to: to || undefined, q: q || undefined };
  const baseKey = ['site-photos', projectId];
  const query = useInfiniteQuery({
    queryKey: [...baseKey, filters],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => sitePhotosApi.list({ projectId, ...filters, offset: pageParam, limit: PAGE_SIZE }),
    getNextPageParam: (last: SitePhotoPage, pages) => (last.hasMore ? pages.length * PAGE_SIZE : undefined),
  });
  // Offset paging: a post made while someone pages back shifts the list by
  // one, so the next page can repeat the last post of this one. Keep the first.
  const posts = useMemo(() => {
    const seen = new Set<number>();
    return (query.data?.pages.flatMap((p) => p.posts) ?? []).filter((p) => !seen.has(p.id) && !!seen.add(p.id));
  }, [query.data]);
  // The person and month lists don't depend on the filters, so keep the last
  // ones this job answered with while a new search or filter loads. Without
  // this, both pickers emptied on every keystroke until the reply came back.
  const firstPage = query.data?.pages[0];
  const [summary, setSummary] = useState<{
    projectId: number; authors: SitePhotoPage['authors']; months: SitePhotoPage['months'];
  } | null>(null);
  useEffect(() => {
    if (firstPage) setSummary({ projectId, authors: firstPage.authors, months: firstPage.months ?? [] });
  }, [firstPage, projectId]);
  const held = summary?.projectId === projectId ? summary : null;
  const authors = firstPage?.authors ?? held?.authors ?? [];
  const months = firstPage?.months ?? held?.months ?? [];
  // The picker shows the month a From/To pair covers exactly; any other range
  // the reader typed shows as "Custom dates" rather than a month it isn't.
  const pickedMonth = monthOfRange(from, to);
  const customRange = (from !== '' || to !== '') && pickedMonth === '';
  const jumpToMonth = (month: string) => {
    const r = monthRange(month);
    setFrom(r?.from ?? '');
    setTo(r?.to ?? '');
  };

  const webUrls = useMemo(() => posts.flatMap((p) => p.photos.map((ph) => ph.webUrl)), [posts]);
  const { thumbs, reset: resetThumbs } = useThumbnailCache(webUrls, graph.silent);
  const showPreviews = async () => {
    try {
      await graph.interactive();
      resetThumbs();
      // The viewer caches misses too — clear them, or it keeps saying
      // "Preview not available" after a successful sign-in (review of #560).
      qc.invalidateQueries({ queryKey: ['site-photo-large'] });
    } catch {
      /* consent pending — placeholders stay */
    }
  };

  // Group by site day (posts already arrive newest day first).
  const days = useMemo(() => {
    const out: Array<{ date: string; posts: SitePhotoPost[] }> = [];
    for (const p of posts) {
      const last = out[out.length - 1];
      if (last && last.date === p.photoDate) last.posts.push(p);
      else out.push({ date: p.photoDate, posts: [p] });
    }
    return out;
  }, [posts]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: baseKey });
  };

  const openViewer = (post: SitePhotoPost, photo: SitePhoto) => {
    // Strictly by time taken (see viewerSequence), not post by post.
    const items: ViewerItem[] = viewerSequence(posts);
    const index = Math.max(0, items.findIndex((i) => i.photo.id === photo.id && i.post.id === post.id));
    setViewer({ items, index });
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (fileRef.current) fileRef.current.value = '';
    if (files.length) setComposerFiles(files);
  };

  // The API's own answer wins once it has arrived (a user with daily-log
  // rights who doesn't lead this job must not be offered an upload the POST
  // will refuse); until then, the permission check keeps the button hidden
  // for anyone who clearly can't post.
  const apiCanPost = query.data?.pages[0]?.canPost;
  const mayPost = canPost && apiCanPost !== false;
  // Edit/Remove on your OWN posts follow their own answer, not "can post":
  // an activity lock blocks new posts only (review of #560).
  const mayManageOwn = canPost && query.data?.pages[0]?.canManageOwn !== false;
  const canAdd = mayPost && apiCanPost === true && !locked && !!project?.jobFolderUrl;
  const filtered = !fixedDate && (authorId || from || to || search.trim());

  return (
    <div className="spt">
      <style>{css}</style>

      <div className="pw-card">
        <div className="spt-bar">
          <div>
            <h2 className="spt-bar-title">Photos</h2>
            <p className="spt-bar-sub">
              {fixedDate
                ? 'Every photo posted for this day. They also appear on the job’s Photos tab.'
                : 'Every photo posted on this job, with or without a daily log, newest first: who posted it, and when and where it was taken.'}
            </p>
          </div>
          {canAdd ? (
            <div className="spt-add">
              {/* No `capture` attribute: on iOS/Android this lets the OS offer
                  the photo library AND the camera, so photos taken earlier
                  (e.g. with no signal on site) can be posted later. */}
              <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={onPick} />
              <Button variant="primary" onClick={() => fileRef.current?.click()}>＋ Add photos</Button>
            </div>
          ) : null}
        </div>

        {mayPost && apiCanPost === true && !locked && project && !project.jobFolderUrl ? (
          <div className="spt-unlinked spt-muted">
            Link this project’s Job Folder (Projects → edit) before posting photos — that’s where the files are stored.
          </div>
        ) : null}

        {!fixedDate ? (
          <div className="spt-filters">
            <div className="spt-filter spt-search">
              <label className="spt-label" htmlFor="spt-search">Search</label>
              <input
                id="spt-search"
                className="spt-input"
                type="search"
                value={search}
                maxLength={200}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Caption, comment or name"
                aria-describedby="spt-search-hint"
              />
            </div>
            <div className="spt-filter">
              <label className="spt-label" htmlFor="spt-month">Month</label>
              <select
                id="spt-month"
                className="spt-input"
                value={customRange ? 'custom' : pickedMonth}
                onChange={(e) => { if (e.target.value !== 'custom') jumpToMonth(e.target.value); }}
              >
                <option value="">All months</option>
                {customRange ? <option value="custom" disabled>Custom dates</option> : null}
                {/* A month jumped to stays listed even if it has since emptied. */}
                {pickedMonth && !months.some((m) => m.month === pickedMonth)
                  ? <option value={pickedMonth}>{monthLabel(pickedMonth)}</option>
                  : null}
                {months.map((m) => (
                  <option key={m.month} value={m.month}>
                    {monthLabel(m.month)} ({m.photos})
                  </option>
                ))}
              </select>
            </div>
            <div className="spt-filter">
              <label className="spt-label" htmlFor="spt-person">Posted by</label>
              <select id="spt-person" className="spt-input" value={authorId} onChange={(e) => setAuthorId(e.target.value)}>
                <option value="">Everyone</option>
                {authors.map((a) => <option key={a.id} value={a.id}>{a.displayName}</option>)}
              </select>
            </div>
            <div className="spt-filter">
              <label className="spt-label" htmlFor="spt-from">From</label>
              <input id="spt-from" className="spt-input" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="spt-filter">
              <label className="spt-label" htmlFor="spt-to">To</label>
              <input id="spt-to" className="spt-input" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </div>
            {filtered ? (
              <button type="button" className="spt-link" onClick={() => { setAuthorId(''); setFrom(''); setTo(''); setSearch(''); }}>Clear filters</button>
            ) : null}
            {/* Say what search covers: nothing reads what is IN a picture, and
                a search that silently misses would read as "not here" (#665). */}
            <p id="spt-search-hint" className="spt-hint">Search covers captions, comments and who posted. It can’t search what’s in a photo.</p>
          </div>
        ) : null}
      </div>

      {graph.needsSignIn && posts.length > 0 ? (
        <div className="spt-unlinked spt-muted" style={{ margin: 0 }}>
          Photo previews need you to sign in to SharePoint.{' '}
          <button type="button" className="spt-link" onClick={showPreviews}>Show previews</button>
        </div>
      ) : null}

      {query.isError ? (
        <div className="spt-error">{(query.error as Error).message}</div>
      ) : query.isLoading ? (
        <p className="spt-muted">Loading photos…</p>
      ) : days.length === 0 ? (
        <div className="pw-card spt-empty spt-muted">
          {/* The search wording only when search is the ONLY filter; with a
              month or a person set too, it may be those that hide the photo. */}
          {filtered
            ? (q && !authorId && !from && !to
              ? `No photos match “${q}”. Search covers captions, comments and who posted.`
              : 'No photos match these filters.')
            : fixedDate ? 'No photos for this day yet.' : 'No photos yet.'}
        </div>
      ) : (
        days.map((day, i) => {
          const log = day.posts.find((p) => p.dailyLog)?.dailyLog ?? null;
          const n = day.posts.reduce((s, p) => s + p.photos.length, 0);
          // A heading where the month changes (#665), by site day like the days.
          const month = monthOf(day.date);
          const newMonth = !fixedDate && month !== '' && (i === 0 || monthOf(days[i - 1].date) !== month);
          return (
            <section key={day.date} className="spt-day">
              {newMonth ? <h3 className="spt-month">{monthLabel(month)}</h3> : null}
              {!fixedDate ? (
                <div className="spt-day-hd">
                  <span className="spt-day-date">{dayLabel(day.date)}</span>
                  <span className="spt-day-count">{n} photo{n === 1 ? '' : 's'}</span>
                  {log && onOpenLog ? (
                    <button type="button" className="spt-log-chip" onClick={() => onOpenLog(log.id)}>
                      Daily log
                      <Badge label={log.status === 'submitted' ? 'Submitted' : 'Draft'} tone={log.status === 'submitted' ? 'success' : 'warning'} />
                      →
                    </button>
                  ) : null}
                </div>
              ) : null}
              <div className="spt-posts">
                {day.posts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    meId={me?.id ?? null}
                    canManageOwn={mayManageOwn}
                    isBroadEditor={isBroadEditor}
                    thumbs={thumbs}
                    site={site}
                    onOpen={(ph) => openViewer(post, ph)}
                    onChanged={invalidate}
                  />
                ))}
              </div>
            </section>
          );
        })
      )}

      {query.hasNextPage ? (
        <div className="spt-more">
          <Button variant="secondary" onClick={() => query.fetchNextPage()} isLoading={query.isFetchingNextPage}>
            Load older photos
          </Button>
        </div>
      ) : null}

      {composerFiles && project?.jobFolderUrl ? (
        <SitePhotoComposer
          projectId={projectId}
          jobNumber={project.jobNumber}
          jobFolderUrl={project.jobFolderUrl}
          files={composerFiles}
          fixedDate={fixedDate}
          site={site}
          getToken={getToken}
          onClose={() => setComposerFiles(null)}
          onPosted={invalidate}
        />
      ) : null}

      {viewer ? (
        <SitePhotoViewer
          items={viewer.items}
          startIndex={viewer.index}
          onClose={() => setViewer(null)}
          getToken={getToken}
          getTokenSilent={graph.silent}
          site={site}
        />
      ) : null}
    </div>
  );
}

// ─── One post ────────────────────────────────────────────────────────────────

function PostCard({
  post, meId, canManageOwn, isBroadEditor, thumbs, site, onOpen, onChanged,
}: {
  post: SitePhotoPost;
  meId: string | null;
  /** May edit/remove their own posts (the API's canManageOwn). */
  canManageOwn: boolean;
  isBroadEditor: boolean;
  thumbs: Record<string, string>;
  site: Site;
  onOpen: (photo: SitePhoto) => void;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(post.caption ?? '');
  const [reply, setReply] = useState('');
  const mine = !!meId && !!post.author && post.author.id.toLowerCase() === meId.toLowerCase();
  // Mirrors the API: the author (while they can write on the job) or a broad
  // editor. Deliberately NOT "can post" — see mayManageOwn.
  const canRemove = isBroadEditor || (mine && canManageOwn);
  const canEdit = mine && canManageOwn;

  const save = useMutation({
    mutationFn: () => sitePhotosApi.update(post.id, { caption: draft }),
    onSuccess: () => { setEditing(false); onChanged(); },
  });
  const removePost = useMutation({ mutationFn: () => sitePhotosApi.removePost(post.id), onSettled: onChanged });
  const removePhoto = useMutation({ mutationFn: (id: number) => sitePhotosApi.removePhoto(id), onSettled: onChanged });
  const comment = useMutation({
    mutationFn: () => sitePhotosApi.addComment(post.id, reply),
    onSuccess: () => { setReply(''); onChanged(); },
  });
  const removeComment = useMutation({ mutationFn: (id: number) => sitePhotosApi.removeComment(id), onSettled: onChanged });

  const layout = post.photos.length === 1 ? ' spt-one' : post.photos.length === 2 ? ' spt-two' : '';
  const taken = takenRange(post.photos);
  const place = placeSummary(post.photos, site);
  const err = [save, removePost, removePhoto, comment, removeComment].find((m) => m.isError)?.error as Error | undefined;

  return (
    <article className="spt-post">
      <div className="spt-post-hd">
        <span className="spt-avatar" aria-hidden="true">{initials(post.author?.displayName)}</span>
        <div className="spt-who">
          <span className="spt-name">{post.author?.displayName ?? 'Unknown'}</span>
          <span className="spt-when">
            Posted {fmtPosted(post.createdAt)}{post.updatedAt ? ' · edited' : ''}
          </span>
        </div>
        <div className="spt-actions">
          {canEdit && !editing ? (
            <button type="button" className="spt-icon" onClick={() => { setDraft(post.caption ?? ''); setEditing(true); }}>Edit</button>
          ) : null}
          {canRemove ? (
            <button
              type="button"
              className="spt-icon spt-danger"
              disabled={removePost.isPending}
              onClick={() => {
                if (window.confirm('Remove this post from the thread? The photos stay in the Job Folder.')) removePost.mutate();
              }}
            >
              Remove
            </button>
          ) : null}
        </div>
      </div>

      {editing ? (
        <div className="spt-edit">
          <textarea className="spt-input" rows={2} maxLength={1000} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Caption" />
          <div className="spt-edit-row">
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={() => save.mutate()} isLoading={save.isPending}>Save</Button>
          </div>
        </div>
      ) : post.caption ? (
        <p className="spt-caption">{post.caption}</p>
      ) : null}

      <div className={`spt-grid${layout}`}>
        {post.photos.map((ph) => {
          const src = thumbs[ph.webUrl];
          return (
            <div key={ph.id} style={{ position: 'relative' }}>
              <button type="button" className="spt-tile" onClick={() => onOpen(ph)} aria-label={`Open photo ${ph.fileName}`}>
                {src ? <img src={src} alt={post.caption ?? ph.fileName} loading="lazy" /> : <span className="spt-tile-ph">{ph.fileName}</span>}
                {ph.takenAt ? <span className="spt-tile-tag">{fmtClock(ph.takenAt)}</span> : null}
              </button>
              {canRemove && post.photos.length > 1 ? (
                <button
                  type="button"
                  className="spt-tile-x"
                  aria-label="Remove this photo"
                  title="Remove this photo (the file stays in the Job Folder)"
                  disabled={removePhoto.isPending}
                  onClick={() => {
                    if (window.confirm('Remove this photo from the post? The file stays in the Job Folder.')) removePhoto.mutate(ph.id);
                  }}
                >
                  ×
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {taken || place ? (
        <div className="spt-meta">
          {taken ? <span>{taken}</span> : null}
          {place ? <span>📍 {place}</span> : null}
        </div>
      ) : null}

      {err ? <div className="spt-error" style={{ margin: 'var(--ew-s-2) var(--ew-s-4) 0' }}>{err.message}</div> : null}

      <div className="spt-comments">
        {post.comments.map((c) => {
          const own = !!meId && !!c.author && c.author.id.toLowerCase() === meId.toLowerCase();
          return (
            <div key={c.id} className="spt-comment">
              <span className="spt-avatar" aria-hidden="true">{initials(c.author?.displayName)}</span>
              <div className="spt-comment-body">
                <b>{c.author?.displayName ?? 'Unknown'}</b>
                <span className="spt-comment-when">{fmtPosted(c.createdAt)}</span>
                <div>{c.body}</div>
              </div>
              {own || isBroadEditor ? (
                <button type="button" className="spt-comment-x" aria-label="Remove comment" onClick={() => removeComment.mutate(c.id)}>×</button>
              ) : null}
            </div>
          );
        })}
        <form
          className="spt-reply"
          onSubmit={(e) => { e.preventDefault(); if (reply.trim()) comment.mutate(); }}
        >
          <input
            className="spt-input"
            value={reply}
            maxLength={2000}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Add a comment…"
            aria-label="Add a comment"
          />
          <Button type="submit" variant="secondary" size="sm" isLoading={comment.isPending} disabled={!reply.trim()}>Post</Button>
        </form>
      </div>
    </article>
  );
}
